import { InfoCircleOutlined } from '@ant-design/icons'
import { Table } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import React, { useMemo, useState } from 'react'
import { useNavigate } from 'react-router'
import styled from 'styled-components'
import { Tooltip } from 'sutra/components/Tooltip/Tooltip'

import { darkBlueFontColor, pageBackgroundGrey } from 'assets/styles/variables'
import { TooltipIconButton } from 'components/TooltipIconButton'
import { AnnouncementBanner } from 'components/ui/AnnouncementBanner'
import { listTableStyles } from 'components/ui/list-table-styles'
import type { FilterOption } from 'features/filters/types'
import { type SharableAgentType } from 'features/trend-detection/components/agent-sharing-calls'
import { AgentSharingModal } from 'features/trend-detection/components/AgentSharingModal/AgentSharingModal'
import {
  ScopeTooltip,
  ScopeTooltipContent,
  ScopeTooltipLabel,
} from 'features/trend-detection/components/scope-tooltip-styles'
import { AgentCell } from 'features/trend-detection/features/agent-list/components/AgentCell'
import { AgentRowKebab } from 'features/trend-detection/features/agent-list/components/AgentRowKebab'
import { AgentsLaunchBanner } from 'features/trend-detection/features/agent-list/components/AgentsLaunchBanner'
import { AgentsTableSkeleton } from 'features/trend-detection/features/agent-list/components/AgentsTableSkeleton/AgentsTableSkeleton'
import {
  type AgentRunStatus,
  LatestFindingsCell,
} from 'features/trend-detection/features/agent-list/components/LatestFindingsCell'
import { QuickCreateCards } from 'features/trend-detection/features/agent-list/components/QuickCreateCards'
import { StatusPill } from 'features/trend-detection/features/agent-list/components/StatusPill'
import type { AgentRunInfo } from 'features/trend-detection/features/agent-list/hooks/use-agents-list'
import type { AgentInsightAgent } from 'features/trend-detection/features/insight-agent/types'
import { trendDetectionFeedPath } from 'features/trend-detection/paths'
import type {
  AgentType,
  BenchmarkingAgentSummary,
  CreatedByUser,
  FilterCriterion,
  IssueAgentResponse,
  SavedFilters,
} from 'features/trend-detection/types'
import {
  formatCriterionExpanded,
  ITEMS_PER_PAGE,
} from 'features/trend-detection/utils/helpers'

// Tooltip caps at 10 values per criterion + "…and N more" so a filter with
// dozens of values doesn't wall-of-text. Cell text is uncapped and relies on
// CSS ellipsis at the container edge.
const TOOLTIP_VALUE_CAP = 10

export type AgentListRow =
  | { kind: 'issue'; agent: IssueAgentResponse }
  | { kind: 'benchmarking'; agent: BenchmarkingAgentSummary }
  | { kind: 'insight'; agent: AgentInsightAgent }

export interface AgentListViewProps {
  agents: IssueAgentResponse[]
  benchmarkingAgents: BenchmarkingAgentSummary[]
  insightAgents?: AgentInsightAgent[]
  agentsPage: number
  setAgentsPage: (page: number) => void
  loadingAgents: boolean
  agentRunInfo: Record<string, AgentRunInfo | null>

  /** Agent types the list's QuickCreateCards may launch, filtered upstream
   * by feature flags. Kept here only for the "more types" dropdown. */
  availableAgentTypes: AgentType[]
  /**
   * Called by the QuickCreateCards on the list page. Navigates to
   * /agents/create?agent_type=<type> — the create page's type field
   * preselects from there.
   */
  onQuickCreate: (type: AgentType) => void
  /** Opens the create page with no preselected type. */
  onStartSetup: () => void

  onSelectAgent: (agent: IssueAgentResponse) => void
  onSelectBenchmarkingAgent: (agent: BenchmarkingAgentSummary) => void
  onSelectInsightAgent?: (agent: AgentInsightAgent) => void
  onDeleteInsightAgent?: (agent: AgentInsightAgent) => void
  /**
   * Flips whether this agent's reports appear on the caller's OWN feed. Per-user
   * and per-agent — it is not the project-wide enable the two sibling agents
   * expose through `onToggleEnabled`.
   */
  onToggleInsightAgentMuted?: (agent: AgentInsightAgent) => void
  /**
   * Called when the user clicks "Edit" in the meatballs menu for an issue agent.
   * Falls back to onSelectAgent if not provided.
   */
  onEditAgent?: (agent: IssueAgentResponse) => void
  /**
   * Called when the user clicks "Edit" in the meatballs menu for a benchmarking agent.
   * Falls back to onSelectBenchmarkingAgent if not provided.
   */
  onEditBenchmarkingAgent?: (agent: BenchmarkingAgentSummary) => void
  onToggleEnabled: (
    agentUuid: string,
    agentType: 'issue_detection' | 'benchmarking',
    newEnabled: boolean,
  ) => void
  onDeleteAgent: (
    agentUuid: string,
    agentType: 'issue_detection' | 'benchmarking',
    agentName: string,
  ) => void
  /** Retry a failed run from the LatestFindingsCell popover. */
  onRerun?: (
    agentUuid: string,
    agentType: 'issue_detection' | 'benchmarking',
  ) => void
  /** Required for sharing modal (project user directory). */
  projectId: string
  /** Filter options for resolving field aliases and UUID values in the Scope column. */
  filterOptions?: FilterOption[]
}

export const AgentListView: React.FC<AgentListViewProps> = ({
  agents,
  benchmarkingAgents,
  insightAgents = [],
  agentsPage,
  setAgentsPage,
  loadingAgents,
  agentRunInfo,

  availableAgentTypes,
  onQuickCreate,
  onStartSetup,

  onSelectAgent,
  onSelectBenchmarkingAgent,
  onSelectInsightAgent,
  onDeleteInsightAgent,
  onToggleInsightAgentMuted,
  onEditAgent,
  onEditBenchmarkingAgent,
  onToggleEnabled,
  onDeleteAgent,
  onRerun,
  projectId,
  filterOptions,
}) => {
  const [shareAgent, setShareAgent] = useState<{
    uuid: string
    name: string
    agentType: SharableAgentType
    isEditor: boolean
  } | null>(null)

  const allRows = useMemo<AgentListRow[]>(() => {
    const issueRows: AgentListRow[] = agents.map((agent) => ({
      kind: 'issue',
      agent,
    }))
    const benchmarkingRows: AgentListRow[] = benchmarkingAgents.map(
      (agent) => ({ kind: 'benchmarking', agent }),
    )
    const insightRows: AgentListRow[] = insightAgents.map((agent) => ({
      kind: 'insight',
      agent,
    }))
    return [...issueRows, ...benchmarkingRows, ...insightRows]
  }, [agents, benchmarkingAgents, insightAgents])

  const totalPages = Math.ceil(allRows.length / ITEMS_PER_PAGE)
  const safePage =
    totalPages > 0 && agentsPage > totalPages ? totalPages : agentsPage

  const navigate = useNavigate()

  const boldDim = (label: string): React.ReactNode => {
    const idx = label.indexOf(': ')
    if (idx === -1) return label
    return (
      <>
        <ScopeDimLabel>{label.slice(0, idx)}</ScopeDimLabel>
        {label.slice(idx)}
      </>
    )
  }

  const getRunStatus = (row: AgentListRow): AgentRunStatus | null => {
    if (row.kind === 'benchmarking') {
      return row.agent.latest_run?.status ?? null
    }
    if (row.kind === 'insight') {
      const s = row.agent.latest_run_status
      if (
        s === 'created' ||
        s === 'running' ||
        s === 'completed' ||
        s === 'failed'
      ) {
        return s
      }
      return null
    }
    return agentRunInfo[row.agent.uuid]?.runStatus ?? null
  }

  const getInsightCount = (row: AgentListRow): number => {
    if (row.kind === 'benchmarking') {
      return row.agent.latest_run?.insight_count ?? 0
    }
    if (row.kind === 'insight') {
      return row.agent.latest_run_insight_count ?? 0
    }
    return agentRunInfo[row.agent.uuid]?.issueCount ?? 0
  }

  const getLastRunTime = (row: AgentListRow): string | null => {
    if (row.kind === 'benchmarking') {
      return row.agent.latest_run?.completed_at ?? null
    }
    if (row.kind === 'insight') {
      return row.agent.latest_run_completed_at
    }
    return agentRunInfo[row.agent.uuid]?.lastRunTime ?? null
  }

  const getIsPaused = (row: AgentListRow): boolean => !row.agent.is_enabled

  const getCreatedByLabel = (row: AgentListRow): string => {
    if (row.agent.is_default) return 'Yogi'
    // `created_by` is optional on all three response shapes until the BE change
    // lands. Falling through to name → email → em-dash keeps the cell honest.
    const created =
      'created_by' in row.agent
        ? (row.agent.created_by as CreatedByUser | null | undefined)
        : undefined
    if (created?.name) return created.name
    if (created?.email) return created.email
    return '—'
  }

  const getCtaKind = (row: AgentListRow): 'latest' | 'earlier' | null => {
    // Paused agents are filtered out of the feed, so both CTAs would deep-link
    // into a filtered-out row — dangling broken affordance. Suppress; the
    // Status pill signals paused, and the user re-opens the agent by
    // unpausing via the kebab.
    if (getIsPaused(row)) return null
    // Any completed run gets a CTA. Insight count picks the label — new
    // insights this period surface "View latest"; a completed run with zero
    // gets "View earlier" so users can still jump into the feed and browse
    // the agent's history. Runs that aren't completed (running, failed,
    // never ran) show no CTA.
    if (getRunStatus(row) !== 'completed') return null
    return getInsightCount(row) > 0 ? 'latest' : 'earlier'
  }

  const handleCtaClick = (
    row: AgentListRow,
    kind: 'latest' | 'earlier',
  ): void => {
    // agent_type lets the feed route to the correct subsection; a bare
    // agent_uuid can't disambiguate issue vs benchmarking vs reports.
    const agentTypeParam =
      row.kind === 'issue'
        ? 'issue_detection'
        : row.kind === 'benchmarking'
          ? 'benchmarking'
          : 'general'
    navigate(
      `${trendDetectionFeedPath(projectId)}?agent_uuid=${row.agent.uuid}&agent_type=${agentTypeParam}&insight_recency=${kind}`,
    )
  }

  const openRow = (row: AgentListRow): void => {
    if (row.kind === 'insight') {
      onSelectInsightAgent?.(row.agent)
      return
    }
    if (row.kind === 'benchmarking') {
      onSelectBenchmarkingAgent(row.agent)
      return
    }
    onSelectAgent(row.agent)
  }

  const editRow = (row: AgentListRow): void => {
    if (row.kind === 'insight') {
      onSelectInsightAgent?.(row.agent)
      return
    }
    if (row.kind === 'benchmarking') {
      if (onEditBenchmarkingAgent) onEditBenchmarkingAgent(row.agent)
      else onSelectBenchmarkingAgent(row.agent)
      return
    }
    if (onEditAgent) onEditAgent(row.agent)
    else onSelectAgent(row.agent)
  }

  const toggleActiveRow = (row: AgentListRow): void => {
    if (row.kind === 'insight') {
      onToggleInsightAgentMuted?.(row.agent)
      return
    }
    const type: 'issue_detection' | 'benchmarking' =
      row.kind === 'benchmarking' ? 'benchmarking' : 'issue_detection'
    onToggleEnabled(row.agent.uuid, type, !row.agent.is_enabled)
  }

  const shareRow = (row: AgentListRow): void => {
    const agentType: SharableAgentType =
      row.kind === 'benchmarking'
        ? 'benchmarking'
        : row.kind === 'insight'
          ? 'insight'
          : 'issue_detection'
    setShareAgent({
      uuid: row.agent.uuid,
      name: row.agent.name,
      agentType,
      isEditor: row.agent.is_editor,
    })
  }

  const deleteRow = (row: AgentListRow): void => {
    if (row.kind === 'insight') {
      onDeleteInsightAgent?.(row.agent)
      return
    }
    const type: 'issue_detection' | 'benchmarking' =
      row.kind === 'benchmarking' ? 'benchmarking' : 'issue_detection'
    onDeleteAgent(row.agent.uuid, type, row.agent.name)
  }

  const columns: ColumnsType<AgentListRow> = [
    {
      title: 'Agent',
      key: 'agent',
      width: 300,
      // Cap the cell so surplus viewport width flows to other columns
      // instead of stretching Agent proportionally. `width` alone is a hint;
      // maxWidth on the actual <td>/<th> enforces the cap.
      onCell: () => ({ style: { maxWidth: 300 } }),
      onHeaderCell: () => ({ style: { maxWidth: 300 } }),
      defaultSortOrder: 'ascend',
      sorter: (a, b) => {
        // Defaults first (Yogi-owned pinned to top), then active agents,
        // then paused agents (paused sinks to bottom), alphabetical within
        // each bucket.
        const defaultDiff =
          Number(b.agent.is_default) - Number(a.agent.is_default)
        if (defaultDiff !== 0) return defaultDiff
        const pausedDiff = Number(getIsPaused(a)) - Number(getIsPaused(b))
        if (pausedDiff !== 0) return pausedDiff
        return a.agent.name.localeCompare(b.agent.name)
      },
      render: (_, row) => (
        <AgentCell
          agentType={
            row.kind === 'benchmarking'
              ? 'benchmarking'
              : row.kind === 'insight'
                ? 'general'
                : 'issue_detection'
          }
          name={row.agent.name}
          isDefault={row.agent.is_default}
          isPaused={getIsPaused(row)}
        />
      ),
    },
    {
      title: 'Scope',
      key: 'scope',
      width: 300,
      onCell: () => ({ style: { maxWidth: 300 } }),
      onHeaderCell: () => ({ style: { maxWidth: 300 } }),
      render: (_, row) => {
        if (row.kind === 'insight') {
          // A Reports Agent has no filter criteria — its scope is the prompt.
          return (
            <Tooltip content={row.agent.prompt}>
              <ScopeText>{row.agent.prompt}</ScopeText>
            </Tooltip>
          )
        }
        if (row.kind === 'benchmarking') {
          const focusFilters = row.agent.focus_group_filters ?? []
          const cohortFilters = row.agent.cohort_filters ?? []
          const summarizeNode = (
            filters: FilterCriterion[],
          ): React.ReactNode => {
            if (filters.length === 0) return 'All'
            const labels = filters
              .map((f) => formatCriterionExpanded(f, filterOptions))
              .filter(Boolean)
            if (labels.length === 0) return 'All'
            // Cell shows only the first criterion; the tooltip lists all.
            // CSS ellipsis at the container edge handles overflow.
            return boldDim(labels[0] ?? '')
          }
          const focusLabels = focusFilters
            .map((f) => ({
              key: f.field + JSON.stringify(f.values),
              label: formatCriterionExpanded(
                f,
                filterOptions,
                TOOLTIP_VALUE_CAP,
              ),
            }))
            .filter((f) => !!f.label)
          const cohortLabels = cohortFilters
            .map((f) => ({
              key: f.field + JSON.stringify(f.values),
              label: formatCriterionExpanded(
                f,
                filterOptions,
                TOOLTIP_VALUE_CAP,
              ),
            }))
            .filter((f) => !!f.label)
          const tooltipContent =
            focusLabels.length > 0 || cohortFilters.length > 0 ? (
              <ScopeTooltipContent>
                {focusLabels.length > 0 && (
                  <>
                    <ScopeTooltipLabel>Benchmark focus</ScopeTooltipLabel>
                    <ul>
                      {focusLabels.map((f) => (
                        <li key={f.key}>{boldDim(f.label)}</li>
                      ))}
                    </ul>
                  </>
                )}
                <>
                  <ScopeTooltipLabel>Filters</ScopeTooltipLabel>
                  {cohortLabels.length > 0 ? (
                    <ul>
                      {cohortLabels.map((f) => (
                        <li key={f.key}>{boldDim(f.label)}</li>
                      ))}
                    </ul>
                  ) : (
                    <div>All</div>
                  )}
                </>
              </ScopeTooltipContent>
            ) : undefined
          return (
            <ScopeTooltip content={tooltipContent} side="bottom">
              <BenchmarkingScopeStack>
                <ScopeText>{summarizeNode(focusFilters)}</ScopeText>
                <CohortRow>
                  <VsLabel>vs.</VsLabel>
                  <CohortText>{summarizeNode(cohortFilters)}</CohortText>
                </CohortRow>
              </BenchmarkingScopeStack>
            </ScopeTooltip>
          )
        }
        const saved = row.agent.config?.filters as SavedFilters | undefined
        const filters: FilterCriterion[] = Array.isArray(saved?.criteria)
          ? saved.criteria
          : []
        if (filters.length === 0) return <ScopeText>All data</ScopeText>
        // Cell: all criteria + all values, comma-joined, CSS ellipsis at
        // container edge. Tooltip: same criteria with per-criterion cap.
        const cellLabels = filters
          .map((f) => formatCriterionExpanded(f, filterOptions))
          .filter(Boolean)
        if (cellLabels.length === 0) return <ScopeText>All data</ScopeText>
        const tooltipLabels = filters
          .map((f) =>
            formatCriterionExpanded(f, filterOptions, TOOLTIP_VALUE_CAP),
          )
          .filter(Boolean)
        const tooltipContent = (
          <ul
            style={{
              margin: 0,
              padding: '2px 0 2px 16px',
              listStyle: 'disc',
              fontSize: 12,
              lineHeight: 1.8,
            }}
          >
            {tooltipLabels.map((l) => (
              <li key={l}>{boldDim(l)}</li>
            ))}
          </ul>
        )
        return (
          <ScopeTooltip content={tooltipContent} side="bottom">
            <ScopeText>
              {cellLabels.map((l, i) => (
                <React.Fragment key={l}>
                  {i > 0 && ', '}
                  {boldDim(l)}
                </React.Fragment>
              ))}
            </ScopeText>
          </ScopeTooltip>
        )
      },
    },
    {
      title: 'Created by',
      key: 'created_by',
      width: 140,
      sorter: (a, b) =>
        getCreatedByLabel(a).localeCompare(getCreatedByLabel(b)),
      render: (_, row) => (
        <CreatedByCell>{getCreatedByLabel(row)}</CreatedByCell>
      ),
    },
    {
      title: 'Status',
      key: 'status',
      width: 90,
      sorter: (a, b) => Number(getIsPaused(a)) - Number(getIsPaused(b)),
      render: (_, row) => (
        <StatusPill status={getIsPaused(row) ? 'paused' : 'active'} />
      ),
    },
    {
      title: 'Latest findings',
      key: 'latest_findings',
      width: 145,
      className: 'no-wrap-header',
      sorter: (a, b) => {
        const countDiff = getInsightCount(b) - getInsightCount(a)
        if (countDiff !== 0) return countDiff
        const aTs = getLastRunTime(a)
          ? new Date(getLastRunTime(a) as string).getTime()
          : 0
        const bTs = getLastRunTime(b)
          ? new Date(getLastRunTime(b) as string).getTime()
          : 0
        return bTs - aTs
      },
      render: (_, row) => {
        // Insight/general agents rerun via a different endpoint (not wired
        // here); default agents can't be manually rerun (BE returns 400).
        const rerunAgentType: 'issue_detection' | 'benchmarking' | null =
          row.kind === 'issue'
            ? 'issue_detection'
            : row.kind === 'benchmarking'
              ? 'benchmarking'
              : null
        const canRerun =
          !!onRerun &&
          rerunAgentType !== null &&
          !row.agent.is_default &&
          row.agent.is_editor === true
        return (
          <LatestFindingsCell
            runStatus={getRunStatus(row)}
            insightCount={getInsightCount(row)}
            isPaused={getIsPaused(row)}
            lastRunTime={getLastRunTime(row)}
            onRerun={
              canRerun && rerunAgentType
                ? () => onRerun!(row.agent.uuid, rerunAgentType)
                : undefined
            }
          />
        )
      },
    },
    {
      title: '',
      key: 'actions',
      /*
       * CTA button + kebab share one column so the row-hover shading reads
       * as one band. Width fits both cell paddings + button + gap + kebab
       * with slack so neither CTA label wraps.
       */
      width: 195,
      align: 'right',
      onCell: () => ({ onClick: (e: React.MouseEvent) => e.stopPropagation() }),
      render: (_, row) => {
        const kind = getCtaKind(row)
        return (
          <ActionCell>
            {kind && (
              <CtaButton
                type="button"
                onClick={() => handleCtaClick(row, kind)}
              >
                {kind === 'latest' ? 'View latest' : 'View earlier'}
              </CtaButton>
            )}
            <AgentRowKebab
              agentName={row.agent.name}
              isEditor={row.agent.is_editor}
              isActive={row.agent.is_enabled}
              isDefault={row.agent.is_default}
              dropdownOffsetY={12}
              handlers={{
                onViewDetails: () => openRow(row),
                onEdit: () => editRow(row),
                onToggleActive: () => toggleActiveRow(row),
                onShare: () => shareRow(row),
                onDelete: () => deleteRow(row),
              }}
            />
          </ActionCell>
        )
      },
    },
  ]

  return (
    <Container>
      <ContentWrapper>
        <TopRow>
          <HeaderBlock>
            <TitleWrapper>
              <Title>Agents</Title>
              <TitleHelpIcon>
                <TooltipIconButton
                  tooltipTitle="Learn more"
                  icon={<HelpIconGlyph aria-label="Open agents help article" />}
                  height={14}
                  width={14}
                  iconFontSize="14px"
                  onClick={() =>
                    window.open(
                      'https://stonly.com/guide/en/agents-4Ydu41N6IT/Steps/5185261',
                      '_blank',
                      'noopener,noreferrer',
                    )
                  }
                />
              </TitleHelpIcon>
            </TitleWrapper>
            <AgentPageSubtitle>
              Set up agents to automatically surface emerging issues,
              competitive gaps, and meaningful changes over time.
            </AgentPageSubtitle>
          </HeaderBlock>

          {allRows.length > 0 && (
            <QuickCreateCards
              onCreateIssueAgent={() => onQuickCreate('issue_detection')}
              onCreateBenchmarkingAgent={() => onQuickCreate('benchmarking')}
              additionalTypes={availableAgentTypes
                .filter((t) => t !== 'issue_detection' && t !== 'benchmarking')
                .map((t) => ({
                  key: t,
                  label: t === 'general' ? 'Reports Agent' : t,
                  onClick: () => onQuickCreate(t),
                }))}
            />
          )}
        </TopRow>

        {!loadingAgents && allRows.length > 0 && (
          <AnnouncementBanner id="agents-launch" expiresAt="2026-06-06">
            <AgentsLaunchBanner onCreateCustomAgent={onStartSetup} />
          </AnnouncementBanner>
        )}

        {loadingAgents && (
          <AgentListSection>
            <AgentsTableSkeleton columns={columns} />
          </AgentListSection>
        )}

        {/* Zero-agents renders nothing here — the Container redirects to
            /agents/create on mount (see its zero-agents effect); this is
            the one-frame gap before that navigation lands. */}

        {!loadingAgents && allRows.length > 0 && (
          <AgentListSection>
            <StyledAgentTable
              columns={columns}
              dataSource={allRows}
              rowKey={(row) => `${row.kind}-${row.agent.uuid}`}
              // scroll.x forces tableLayout: 'fixed' (the actual width
              // enforcement). Value is the sum of column widths.
              scroll={{ x: 1157 }}
              pagination={{
                current: safePage,
                total: allRows.length,
                pageSize: ITEMS_PER_PAGE,
                onChange: (page) => setAgentsPage(page),
                showSizeChanger: false,
                showTotal: (total, range) =>
                  `${range[0]}-${range[1]} of ${total} agents`,
              }}
              onRow={(row) => {
                const open = (): void => {
                  if (row.kind === 'insight') {
                    onSelectInsightAgent?.(row.agent)
                    return
                  }
                  if (row.kind === 'benchmarking') {
                    onSelectBenchmarkingAgent(row.agent)
                    return
                  }
                  onSelectAgent(row.agent)
                }
                return {
                  onClick: open,
                  onKeyDown: (e: React.KeyboardEvent) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault()
                      open()
                    }
                  },
                  tabIndex: 0,
                  role: 'button',
                  style: { cursor: 'pointer' },
                }
              }}
            />
          </AgentListSection>
        )}
        <AgentSharingModal
          agentName={shareAgent?.name ?? ''}
          agentUuid={shareAgent?.uuid ?? ''}
          projectId={projectId}
          isEditor={shareAgent?.isEditor ?? false}
          open={shareAgent !== null}
          onClose={() => setShareAgent(null)}
          agentType={shareAgent?.agentType ?? 'issue_detection'}
        />
      </ContentWrapper>
    </Container>
  )
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  background-color: ${pageBackgroundGrey};
  overflow-y: auto;
  padding: 20px;
  background: white;
`

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  justify-content: flex-start;
  gap: 24px;
  min-width: 0;
  width: 100%;
  flex: 1;
`

const TitleWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const Title = styled.h1`
  font-size: 28px;
  font-weight: 600;
  line-height: 1.2;
  color: ${darkBlueFontColor};
  margin: 0;
  text-align: left;
`

const TitleHelpIcon = styled.span`
  display: flex;
  align-items: center;
`

const HelpIconGlyph = styled(InfoCircleOutlined)`
  color: #717680;
`

const AgentPageSubtitle = styled.p`
  margin: 0;
  font-family: 'Poppins', sans-serif;
  font-weight: 500;
  font-size: 14px;
  line-height: 20px;
  letter-spacing: 0;
  color: #717680;
`

const TopRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  width: 100%;
  /* Wrap when the viewport is too narrow to hold header + cards on one row.
   * Prevents the cards from overflowing/overlapping the title. */
  flex-wrap: wrap;
`

const HeaderBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  flex: 0 1 452px;
  max-width: 452px;
  min-width: 0;
`

const AgentListSection = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 16px;
  min-width: 0;
`

const StyledAgentTable = styled(Table<AgentListRow>)`
  ${listTableStyles}
`

const ActionCell = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  justify-content: flex-end;
`

const ScopeText = styled.span`
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 300px;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  color: #28364f;
`

const BenchmarkingScopeStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
`

const CohortRow = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
`

const CohortText = styled.span`
  font-size: 13px;
  font-weight: 500;
  line-height: 18px;
  color: #717680;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
`

const VsLabel = styled.span`
  font-size: 13px;
  font-weight: 600;
  line-height: 18px;
  color: #717680;
  flex-shrink: 0;
`

const ScopeDimLabel = styled.span`
  font-weight: 500;
`

const CreatedByCell = styled.span`
  font-size: 14px;
  font-weight: 500;
  color: #28364f;
  line-height: 20px;
`

const CtaButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  width: 111px;
  height: 40px;
  padding: 10px 14px;
  background: #ffffff;
  border: 1px solid #d5d7db;
  border-radius: 8px;
  font-family: inherit;
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
  color: #28364f;
  cursor: pointer;
  /* Pin against the surrounding flex row's default shrink behavior. */
  flex-shrink: 0;
  white-space: nowrap;
  transition:
    border-color 120ms ease,
    background 120ms ease;

  &:hover {
    border-color: rgba(40, 54, 79, 0.4);
    background: #f9fafb;
  }

  &:focus-visible {
    outline: 2px solid #4f72a4;
    outline-offset: 2px;
  }
`
